import type { ChatSource } from "@mirage/mosaics/Chat/types";
import type { TabInfo } from "@mirage/service-extension-tab-ingestion/service/types";
import { urlToContentID } from "@mirage/shared/util/urlRulesUtil";
import type {
  ComposeAssistantConversationMessage,
  ComposeAssistantConversationMessageMessage,
} from "./assist-message";

export interface ComposeSession {
  dataId: string; // Server public id
  id: string; // Client private id
  messagesHistory: ComposeAssistantConversationMessage[];
  sources: ChatSource[];
  artifacts: ComposeArtifact[];
  lastUpdated?: number;
  rev?: number;
  customMode?: "onboarding" | "other";
}

export function isComposeAssistantConversationMessageMessage(
  message: ComposeAssistantConversationMessage,
): message is ComposeAssistantConversationMessageMessage {
  return message.type === "message";
}

export function getSourceUUID(source: ChatSource): string | undefined {
  switch (source.type) {
    case "dash_search_result":
    case "mirage_search_result":
      return source.searchResult.uuid;
    case "recommendation":
      return source.recommendation.uuid;
    case "transient":
      return source.uuid;
    case "local_file_content":
      return source.uuid;
    case "stack_item":
      return source.stackItem.uuid;
    case "summarizable_result":
      return source.summarizableResult.uuid;
    case "browser_tab_cache":
    case "browser_tab_snapshot":
      return getBrowserTabSnapshotUUID(source.tabInfo);
    case "fss_file":
    case "fss_folder":
      return source.fileId;
    default:
      source satisfies never;
      return undefined;
  }
}

export function getSourceTitle(source: ChatSource): string {
  switch (source.type) {
    case "dash_search_result":
    case "mirage_search_result":
      return source.searchResult.title || "";
    case "recommendation":
      return source.recommendation.title;
    case "transient":
      return source.title;
    case "local_file_content":
      return source.fileName;
    case "stack_item":
      return source.stackItem.name || "";
    case "summarizable_result":
      return typeof source.summarizableResult.title === "string"
        ? source.summarizableResult.title || ""
        : "";
    case "browser_tab_cache":
    case "browser_tab_snapshot":
      return source.tabInfo.title || "";
    case "fss_file":
      // TODO (yong): Fill this in when we have the data
      return "";
    case "fss_folder":
      // TODO (yong): Fill this in when we have the data
      return "";
    default:
      source satisfies never;
      return "";
  }
}

export function getSourceUrl(source: ChatSource): string | undefined | null {
  switch (source.type) {
    case "dash_search_result":
    case "mirage_search_result":
      return source.searchResult.url;
    case "recommendation":
      return source.recommendation.url;
    case "transient":
      return source.url;
    case "local_file_content":
      return source.url;
    case "stack_item":
      return source.stackItem.url || "";
    case "summarizable_result":
      return source.summarizableResult.url;
    case "browser_tab_cache":
    case "browser_tab_snapshot":
      return source.tabInfo.url || "";
    case "fss_file":
      // TODO (yong): Fill this in when we have the data
      return "";
    case "fss_folder":
      // TODO (yong): Fill this in when we have the data
      return "";
    default:
      source satisfies never;
      return undefined;
  }
}

export type ComposeArtifact = ComposeArtifactMarkdownDraft;

export interface ComposeArtifactMarkdownDraft {
  id: string;
  type: "markdown_draft";
  markdownContent: string;
  draftConfig: ComposeAssistantDraftConfig;
}

export function getFirstMarkdownArtifact(
  artifacts: ComposeArtifact[],
): ComposeArtifactMarkdownDraft | undefined {
  return artifacts.find((a) => a.type === "markdown_draft");
}

export interface ComposeAssistantDraftConfig {
  contentType?: ContentTypeOption | null;
  contentDescription?: string;
  voiceID?: string;
  templateID?: string | null; // explicit null means 'Start from scratch' was selected
}

export const CONTENT_TYPES = [
  "email",
  "message",
  "report",
  "proposal",
  "social_media_post",
  "meeting_agenda",
] as const;
export type ContentTypeOption = (typeof CONTENT_TYPES)[number];

export const PRE_CONFIGURED_VOICE_IDS = [
  "preset_neutral",
  "preset_persuasive",
  "preset_instructional",
  "preset_narrative",
  "preset_informal",
  "preset_analytical",
] as const;
export type PreConfiguredVoiceID = (typeof PRE_CONFIGURED_VOICE_IDS)[number];

export const DEFAULT_PRECONFIGURED_VOICE_ID: PreConfiguredVoiceID =
  PRE_CONFIGURED_VOICE_IDS[0];

export function isPreConfiguredVoiceID(
  voiceID: string,
): voiceID is PreConfiguredVoiceID {
  return PRE_CONFIGURED_VOICE_IDS.includes(voiceID as PreConfiguredVoiceID);
}

export type InputContext = {
  type: "selection";
  selectedText: string;
};

export const getTemplateModificationHistoryFromMessages = (
  messages: ComposeAssistantConversationMessage[],
) => {
  return messages
    .filter((m) => m.role === "user")
    .filter((m) => m.type === "message")
    .map((m) => m.text || "");
};

export const getVoiceModificationHistoryFromMessages = (
  messages: ComposeAssistantConversationMessage[],
) => {
  return messages
    .filter((m) => m.role === "user")
    .filter((m) => m.type === "message")
    .map((m) => m.text || "");
};

function getBrowserTabSnapshotUUID(tabInfo: Partial<TabInfo>): string {
  // UUID identifies a snapshot by both the tab ID and the content ID
  const contentID = urlToContentID(tabInfo.url ?? "");
  return `browser-tab-${tabInfo.id ?? "cache"}-${contentID}`;
}
